import {
    BackupFilters,
    ConversationFilterObject,
    EvalType as ConversationsEvalType,
} from "components/Conversations/Stores/ConversationsStore";
import { initRBCFilterItems, IRBCFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/RBCFilterStore";
import {
    IOutlineFilter,
} from "components/Conversations/Views/Drawer/components/DeepFilter/OutlineFilterStore";
import {
    IWordsPhrasesModel,
    initWordsPhrasesItems,
} from "components/Conversations/Views/Drawer/components/DeepFilter/WordsPhrasesStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import _, { isEqual } from "lodash";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import moment, { Moment } from "moment";
import {
    ApplicationFilterDomain, ApplicationFiltersService, CreateApplicationFilter,
    UpdateApplicationFilter
} from "services/ApplicationFiltersService";
import { ServiceError } from "services/BaseService";
import { v4 as uuidv4 } from 'uuid';
import { AuthStore } from "../AuthStore";
import { AsyncTaskStatus } from "../BaseStore";
import { DateRangeLabel, DateReferenceOption } from "../ComponentStores/DatePickerComponentStore";
import LocalStorage from "../LocalStorage";
import type { IRootStore } from "../RootStore";
import { AcxStore } from "../RootStore";
import { FilterKey, ApplicationFilterItemsStore } from "./ApplicationFilterItemsStore";
import { ApplicationFiltersInstanceStore } from "./ApplicationFiltersInstanceStore";
import { CallDirection } from "./Filters/CallDirection";
import { EvaluationType } from "./Filters/EvaluationType";
import { HipaaCompliance } from "./Filters/HipaaCompliance";
import { MediaType } from "./Filters/MediaType";
import { Sentiment } from "./Filters/Sentiment";
import { PatientJourneyStage } from "./Filters/PatientJourneyStage";
import { Language } from "./Filters/Language";
import { ModuleService } from "services/ModuleService";
import { ModulePreview } from "components/Admin/Organizations/types/Module.type";
import { SlimLLMClassifierResponse } from "models/LLMs";
import { SafetyEvent, SafetyEventType } from "./Filters/SafetyEvent";
import { Operator } from "models/Rule";
import { generateFilterHtml } from "components/Conversations/Views/Drawer/components/Subscriptions/subscriptionRenderedFilter";
import { ApplicationFilterValuesStore } from "./ApplicationFilterValuesStore";
import { ExtendedMetadataClassifier } from "models/ClassifierModel";
import { isGroupableExtendedMetadataField } from "utils/extendedMetadata";

const parseCommaSeparatedValues = (value?: string) => {
    if (!value) return [];
    return value
        .split(",")
        .map((entry) => entry.trim())
        .filter(Boolean);
};

export const buildExtendedMetadataGroupedFromStrings = (
    extendedMetadata?: Record<string, string>,
    extendedMetadataGrouped?: Record<string, ExtendedMetadataGrouped>,
    extendedMetadataFields?: ExtendedMetadataClassifier[],
): Record<string, ExtendedMetadataGrouped> | undefined => {
    if (!extendedMetadata) return extendedMetadataGrouped;

    const updatedGroupedEntries: Record<string, ExtendedMetadataGrouped> = {
        ...(extendedMetadataGrouped ?? {}),
    };

    Object.entries(extendedMetadata).forEach(([key, value]) => {
        // Only process if the field is groupable
        const filterKey = `extendedMetadata.${key}`;
        const isGroupable = extendedMetadataFields
            ? isGroupableExtendedMetadataField(extendedMetadataFields, filterKey)
            : false;

        if (!isGroupable) return;

        const parsedValues = parseCommaSeparatedValues(value);
        if (parsedValues.length === 0) return;
        updatedGroupedEntries[key] = {
            contains: true,
            values: parsedValues,
        };
    });

    const isEmpty = Object.values(updatedGroupedEntries).length === 0;

    return isEmpty ? undefined : updatedGroupedEntries;
};

type SavedConversationsFilters = {
    label: string;
    value: ConversationFilterObject & {
        startDate: string | moment.Moment;
        endDate: string | moment.Moment;
        dateReference: DateReferenceOption;
    };
};

// This refers to "themes" as topics and is the legacy themes via topic.
// Persisted under the "topics" filter key, backed by ThemeViaTopicsFastReporting*.
// The Primary Driver equivalent is SimpleTheme.
export interface SimpleTopicCluster {
    id: string;
    topicLabel: string;
}

/**
 * A Theme resolved through the Primary Driver, persisted under the "themes" filter key and
 * backed by the Theme* query contract. Sibling of {@link SimpleTopicCluster}, never a
 * replacement — the two Theme sources never merge or fall back to one another.
 */
export interface SimpleTheme {
    id: string;
    themeLabel: string;
}

export type PrimaryTopic = {
    topicNumber: number;
    topicLabel: string;
};

export type PrimaryDriver = {
    driverIndex: number;
    driverName: string;
    themeNumber?: number;
    sourceTopicName?: string;
};

export interface ContactTypeOption {
    value: string;
    label: string;
}

export interface PatientJourneyStageOption {
    value: PatientJourneyStage;
    label: string;
}

export interface LanguageFilterObject {
    languageValue: Language;
    localeId?: number;
}

export interface DataLabel {
    id: string;
    name: string;
}

export interface ModuleScore {
    licensedModuleId: string;
    operator: Operator | undefined;
    moduleScore: number
}

export interface QuestionResultFilter {
    licensedModuleId: string;
    questionId: string;
    tagIds: string[];
}

export interface ExtendedMetadataGrouped {
    contains: boolean;
    values: string[];
}

export interface ApplicationFilters {
    startDate?: string | moment.Moment;
    endDate?: string | moment.Moment;
    dateReferenceOption: DateReferenceOption;
    dateRangeLabel?: DateRangeLabel;
    /** The start of the range, in whole minutes, and part of it. Zero means no lower bound. */
    minCallDuration?: number;
    /**
     * The end of the range, in whole minutes, which the range stops short of: a maximum of 5 matches
     * 4:59.999 but not 5:00.000. Zero means no upper bound.
     *
     * Exclusive so that a range and the group-by bucket it describes hold the same conversations.
     * The "2-5 min" bucket runs 2:00.000 through 4:59.999, because 5:00.000 opens the next bucket --
     * an inclusive bound would hand back a conversation the bar did not count. Whole minutes still
     * describe that exactly: the precision comes from the comparison, not from the number, so the
     * slider and the chips keep showing plain minutes.
     */
    maxCallDuration?: number;
    minMessageCount?: number;
    maxMessageCount?: number;
    evaluationTypes: EvaluationType[];
    hierarchyIds: string[];
    selectedCategories?: string[];
    selectedSubcategories?: string[];
    agentIds?: string[];
    agentManagerIds?: string[];
    mediaTypes: MediaType[];
    callDirections: CallDirection[];
    clientCallId?: string;
    meta1?: string;
    meta2?: string;
    meta3?: string;
    meta4?: string;
    meta5?: string;
    extendedMetadata?: Record<string, string>;
    extendedMetadataGrouped?: Record<string, ExtendedMetadataGrouped>;
    beginningSentiment?: Sentiment;
    endingSentiment?: Sentiment;
    eddyEffectStatus?: boolean;
    safetyEvent?: SafetyEvent;
    hipaaCompliance?: HipaaCompliance;
    topics?: SimpleTopicCluster[];
    themes?: SimpleTheme[];
    primaryTopics?: PrimaryTopic[];
    drivers?: PrimaryDriver[];
    containsTopics: boolean;
    containsThemes?: boolean;
    containsPrimaryTopics: boolean;
    containsDrivers?: boolean;
    wordsAndPhrasesSearchString?: string;
    wordsAndPhrasesItems?: IWordsPhrasesModel[];
    wordsAndPhrasesIsInputStandard?: boolean;
    rbcFilterItems?: IRBCFilter[];
    outlineSegmentFilterItems?: IOutlineFilter[];
    contactTypes?: string[];
    patientJourneyStages?: PatientJourneyStage[],
    favoriteConversations?: boolean;
    containsDataLabels?: boolean;
    containsResearchLabels?: boolean;
    dataLabels?: DataLabel[];
    modelResults?: SlimLLMClassifierResponse[];
    moduleScores?: ModuleScore[];
    questionResults?: QuestionResultFilter[];
    embeddedTranscriptionQuery?: string;
    embeddedTranscriptionQueryThreshold?: number;
    languages?: LanguageFilterObject[]
}

export interface SavedFilter {
    id: string;
    name: string;
    userId?: string;
    createdBy?: string;
    value: ApplicationFilters;
    domain: ApplicationFilterDomain;
    hasDashboardSubscription?: boolean;
    hasOtherApplicationFilterSubscription?: boolean;
    applicationFilterSubscriptionId?: string;
}

export interface LocalSavedFilter {
    id: string;
    label: string;
    filters: ApplicationFilters;
}

interface LastSelectedFilters {
    id?: never;
    name?: never;
    filters: ApplicationFilters;
}

@AcxStore
export class ApplicationFiltersStore extends ApplicationFiltersInstanceStore {
    public static Tasks = {
        VALIDATE_WORDS_AND_PHRASES: "Validate Words and Phrases",
        LOAD_HIERARCHY_LABELS: "Load Hierarchy Labels",
        PERSIST_LOCAL_FILTERS: "Persist Local Filters",
        LOAD_SAVED_FILTERS: "Load Saved Filters",
        SAVE_FILTER: "Save Filter",
        UPDATE_FILTER: "Update Filter",
        DELETE_FILTER: "Delete Filter",
        LOAD_AGENTS: "Load Agents",
    } as const;

    public static AllSavedFilterDomains = [
        ApplicationFilterDomain.Signals,
        ApplicationFilterDomain.Conversations,
        ApplicationFilterDomain.User,
        ApplicationFilterDomain.Organization
    ] as const satisfies readonly ApplicationFilterDomain[];

    private static SAVED_FILTERS_KEY = "SavedFilters";
    private static LAST_SELECTED_FILTERS_KEY = "LastSelected";
    private static OLD_CONVERSATIONS_SAVED_FILTERS_KEY = "filterItems";
    private static OLD_CONVERSATIONS_BACKUP_FILTERS_KEY =
        "backupConversationFilters";

    readonly authStore: AuthStore;
    @observable quickFiltersStore: ApplicationFiltersInstanceStore;

    readonly applicationFiltersService: ApplicationFiltersService;
    readonly moduleService: ModuleService = new ModuleService();

    @observable licensedModules: {
    dataLabelingModules: ModulePreview[];
    researchModules: ModulePreview[];
    } | null = null;


    localStorage: LocalForage;
    conversationsLocalStorage: LocalForage;

    currentlyAppliedFilters?: ApplicationFilters;
    @observable safetyEventTypes: SafetyEventType[] = [];

    @observable savedFiltersByDomain: Record<
        ApplicationFilterDomain,
        SavedFilter[]
    > = {
        Organization: [],
        User: [],
        Conversations: [],
        Signals: [],
    };
    @computed get savedFilters() {
        return Object.values(this.savedFiltersByDomain).flatMap(
            (filters) => filters,
        );
    }
    @computed get mySavedFilters() {
        const filters = [...this.savedFiltersByDomain.User, ...this.savedFiltersByDomain.Conversations, ...this.savedFiltersByDomain.Signals]
        return filters.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
    }
    @computed get sharedSavedFilters() {
        return this.savedFiltersByDomain.Organization;
    }
    @computed get ownedSavedFilters() {
        return this.savedFilters.filter((filter) => filter.userId === this.authStore._user.profile.sub)
    }
    @computed get notOwnedSavedFilters() {
        return this.savedFilters.filter((filter) => filter.userId !== this.authStore._user.profile.sub)
    }

    @observable selectedSavedFilter?: SavedFilter;

    override get dateReference() {
        return this.quickFiltersStore?.dateReferenceOption ?? this.dateReferenceOption
    }

    private organizationId: string;

    constructor(public rootStore: IRootStore) {
        super(rootStore, "Application Filters");
        makeObservable(this);

        // Default user to interaction date
        this.setDateReferenceOption(DateReferenceOption.InteractionDate);

        this.authStore = rootStore.getStore(AuthStore);
        this.localStorage = rootStore
            .getStore(LocalStorage)
            .getLocalStore("ApplicationFilters");
        this.conversationsLocalStorage = rootStore
            .getStore(LocalStorage)
            .getLocalStore("ConversationsStore");

        this.applicationFiltersService = new ApplicationFiltersService();
        this.quickFiltersStore = this.toQuickFilters();

        reaction(
            () => this.authStore.orgStore?.selectedOrganization,
            async (organization) => {
                if (!organization) return;

                const oldOrganizationId = this.organizationId;
                if (oldOrganizationId) {
                    // save filters on org change
                    this.saveCurrentAsLastSelectedFilters(); 
                }

                this.organizationId = organization.id;

                this.resetForOrganizationChange();
                this.applyLastSelectedFilters();
                this.quickFiltersStore = this.toQuickFilters();

                await this.persistAllLocalFilters();
                this.cleanupAllLocalFilters();
                this.loadSavedFiltersByDomain();
                const safetyEventTypes = await this.applicationFiltersService.getSafetyEventTypes();
                runInAction(() => {
                    this.safetyEventTypes = safetyEventTypes;
                });

            },
            { fireImmediately: true },
        );

        // Need to make sure we save current filters when outside sources
        // change
        reaction(
            () => [
                this.datePickerStore?.beginDate,
                this.datePickerStore?.endDate,
                this.datePickerStore?.referenceOption,
            ],
            () => this.debouncedSaveCurrentAsLastSelectedFilters(),
        );
    }

    private resetSavedFiltersByDomain() {
        this.savedFiltersByDomain = {
            Organization: [],
            User: [],
            Signals: [],
            Conversations: [],
        };
    }

    private resetForOrganizationChange() {
        this.resetSavedFiltersByDomain();
        this.selectedSavedFilter = undefined;

        this.resetFilters();
    }

    public resetAllFilters(){
       this.setSelectedSavedFilters();
       this.resetFilters();
       this.quickFiltersStore.resetFilters();
    }

    public getKnownHierarchyLabels(): Record<string, string> {
        return {
            ...(this.quickFiltersStore?.hierarchyLabels ?? {}),
            ...this.hierarchyLabels,
        };
    }

    /**
     * Copies the current quick filter values onto the primary filters store so downstream
     * requests (which rely on the main store's values) include the quick selections.
     */
    @action
    public syncQuickFiltersIntoMain() {
        if (!this.quickFiltersStore) return;

        this.setStartDate(this.quickFiltersStore.startDate);
        this.setEndDate(this.quickFiltersStore.endDate);
        this.setDateReferenceOption(this.quickFiltersStore.dateReferenceOption);
        this.setDateRangeLabel(this.quickFiltersStore.dateRangeLabel);
        this.setHierarchyIds(this.quickFiltersStore.hierarchyIds ?? []);
        this.setHierarchyLabels(this.getKnownHierarchyLabels());
        void this.hydrateHierarchyLabels(this.quickFiltersStore.hierarchyIds ?? []);
        this.setSelectedCategories(
            this.quickFiltersStore.selectedCategories ?? [],
            this.quickFiltersStore.selectedSubcategories ?? [],
        );
    }


    /**
     * ----- Local Filter Support & Persistence -----
     */

    private storageKeyForCurrentOrganization(key: string) {
        return `${key}-${this.organizationId}`;
    }

    private oldConvosStorageKeyForCurrentOrganization(key: string) {
        return `${key}${this.organizationId}`;
    }

    /**
     * Persists any local filters to the database and adds
     * them to savedFiltersByDomain in this store. If a filter
     * fails to persist it's discarded and lost forever. Ignores
     * local filters without a name or an empty string for a name.
     */
    private async persistAllLocalFilters() {
        this.setupAsyncTask(
            ApplicationFiltersStore.Tasks.PERSIST_LOCAL_FILTERS,
            async () => {
                const filters = await this.getLocalSavedFilters();
                const convosFilters =
                    await this.getLocalConversationsSavedFilters();

                const allLocalFilters = [...filters, ...convosFilters];

                if (allLocalFilters.length === 0) return;

                // Would've liked to use Promise.all here but it did not want
                // to cooperate for whatever reason.
                for (const filter of allLocalFilters) {
                    if (!filter.label || filter.label === "") continue;

                    await this.saveLocalFilter(filter);
                }

                await this.cleanupAllLocalFilters();
            },
        );
    }

    private async saveLocalFilter(
        filter: LocalSavedFilter,
    ): Promise<SavedFilter> {
        const createFilter: CreateApplicationFilter = {
            name: filter.label,
            domain: ApplicationFilterDomain.Conversations, // all old local filters were created in/for convos
            value: filter.filters,
        };

        return this.applicationFiltersService.createApplicationFilter(
            createFilter,
        );
    }

    @action
    private async getLocalSavedFilters() {
        let stringifiedFilters = await this.localStorage.getItem<string>(
            this.storageKeyForCurrentOrganization(
                ApplicationFiltersStore.SAVED_FILTERS_KEY,
            ),
        );

        if (!stringifiedFilters) stringifiedFilters = "[]";

        const filters: LocalSavedFilter[] = JSON.parse(stringifiedFilters);
        return filters;
    }

    @action
    private async getLocalConversationsSavedFilters() {
        let stringifiedConversationsFilters =
            await this.conversationsLocalStorage.getItem<string>(
                this.oldConvosStorageKeyForCurrentOrganization(
                    ApplicationFiltersStore.OLD_CONVERSATIONS_SAVED_FILTERS_KEY,
                ),
            );

        if (!stringifiedConversationsFilters)
            stringifiedConversationsFilters = "[]";

        const filters: LocalSavedFilter[] = (
            JSON.parse(
                stringifiedConversationsFilters,
            ) as SavedConversationsFilters[]
        ).map((saved) => ({
            id: uuidv4(),
            label: saved.label,
            filters:
                ApplicationFiltersStore.savedConversationsFiltersToApplicationFilters(
                    saved.value,
                ),
        }));

        return filters;
    }

    /**
     * Removes all local saved filters from browser indexed dbs
     * @returns
     */
    private async cleanupAllLocalFilters() {
        return await Promise.all([
            this.conversationsLocalStorage.removeItem(
                this.oldConvosStorageKeyForCurrentOrganization(
                    ApplicationFiltersStore.OLD_CONVERSATIONS_SAVED_FILTERS_KEY,
                ),
            ),
            this.localStorage.removeItem(
                this.storageKeyForCurrentOrganization(
                    ApplicationFiltersStore.SAVED_FILTERS_KEY,
                ),
            ),
        ]);
    }

    /**
     * ----- End Local Filter Support & Persistence -----
     */

    @computed
    public get isSelectedSavedFilterEditable(): boolean {
        if (!this.selectedSavedFilter) return false;
        return this.authStore._user.profile.sub === this.selectedSavedFilter.userId;
    }

    public getSavedFiltersByDomains(domains: ApplicationFilterDomain[] | readonly ApplicationFilterDomain[]) {
        const filters: SavedFilter[] = [];

        for (const domain of domains) {
            filters.push(...this.savedFiltersByDomain[domain]);
        }

        return filters;
    }

    @action
    public loadSavedFiltersByDomain() {
        return this.setupAsyncTask(
            ApplicationFiltersStore.Tasks.LOAD_SAVED_FILTERS,
            async () => {
                const filtersByDomain =
                    await this.applicationFiltersService.getApplicationFiltersByDomain();

                this.resetSavedFiltersByDomain();

                for (const [domain, filters] of Object.entries(
                    filtersByDomain,
                ) as [ApplicationFilterDomain, SavedFilter[]][]) {
                    if (!domain) continue;

                    this.addSavedFiltersByDomain(filters, domain);
                }

                this.forceUpdateSelectedSavedFilter();
            },
        );
    }

    /**
     * Loads in the last selected filters for the users current organization from session
     * storage. If none exist it will look for backupConversationFilters in session storage.
     * If those exist they will be loaded and then deleted.
     * @returns
     */
    @action
    public applyLastSelectedFilters() {
        const stringifiedFilters = sessionStorage.getItem(
            this.storageKeyForCurrentOrganization(
                ApplicationFiltersStore.LAST_SELECTED_FILTERS_KEY,
            ),
        );

        let filters: LastSelectedFilters;
        if (!stringifiedFilters) {
            const oldBackupFiltersStringified = sessionStorage.getItem(
                ApplicationFiltersStore.OLD_CONVERSATIONS_BACKUP_FILTERS_KEY,
            );

            if (!oldBackupFiltersStringified) return;

            const backupFilters: BackupFilters = JSON.parse(
                oldBackupFiltersStringified,
            );
            if (backupFilters.orgId !== this.organizationId) return;

            sessionStorage.removeItem(
                ApplicationFiltersStore.OLD_CONVERSATIONS_BACKUP_FILTERS_KEY,
            );

            filters = {
                filters:
                    ApplicationFiltersStore.backupConversationsFiltersToApplicationFilters(
                        backupFilters,
                    ),
            };
        } else {
            filters = JSON.parse(stringifiedFilters);
        }

        this.applyExternalFilters(filters.filters);
    }

    @action
    public saveCurrentAsLastSelectedFilters() {
        const filters: LastSelectedFilters = {
            filters: this.toFilterObject(),
        };

        return sessionStorage.setItem(
            this.storageKeyForCurrentOrganization(
                ApplicationFiltersStore.LAST_SELECTED_FILTERS_KEY,
            ),
            JSON.stringify(filters),
        );
    }

    public debouncedSaveCurrentAsLastSelectedFilters = _.debounce(() => {
        if (!this.organizationId) return;
        this.saveCurrentAsLastSelectedFilters();
    }, 500);

    protected override onFilterChange(): void {
        this.debouncedSaveCurrentAsLastSelectedFilters();
    }

    @action
    public async applySavedFilters(filtersId: string) {
        if (!this.savedFilters) return;

        const filters = this.savedFilters.find(
            (filters) => filters.id === filtersId,
        );
        if (!filters) return;
        if (typeof filters.value === "string")
            filters.value = JSON.parse(filters.value) as ApplicationFilters;

        this.selectedSavedFilter = filters;
        const filterItemsStore = this.rootStore.getStore(ApplicationFilterItemsStore);
        filters.value.extendedMetadataGrouped =
            buildExtendedMetadataGroupedFromStrings(
                filters.value.extendedMetadata,
                filters.value.extendedMetadataGrouped,
                filterItemsStore.extendedMetadataFields,
            );
        // If a user saved w/o date range, don't apply it to 
        const quickFilters = _.cloneDeep(filters.value)
        if (!filters.value.startDate && !filters.value.endDate) {
            quickFilters.startDate = this.quickFiltersStore.startDate;
            quickFilters.endDate = this.quickFiltersStore.endDate
        }
        this.quickFiltersStore.applyExternalFilters(quickFilters);
        this.applyExternalFilters(_.cloneDeep(filters.value));

        // make sure we never send filter values they dont have access to
        if (!this.authStore.canUserView("Safety Events Model")) {
            this.setSafetyEvent(undefined);
        }

        if (!this.authStore.canUserView("HIPAA Compliance Model")) {
            this.setHipaaCompliance(undefined);
        }

        if (!this.authStore.canUserView("Topics")) {
            this.setTopics([]); // Themes, via the legacy Fast Reporting Topic path
            this.setContainsTopics(true);
            this.setPrimaryTopics([]);
        }

        if (!this.authStore.canUserView("Drivers")) {
            this.setThemes([]); // Themes, via the Primary Driver
            this.setContainsThemes(true);
            this.setDrivers([]);
            this.setContainsDrivers(true);
        }

        const { dataLabelingModules, researchModules } = await this.getLicensedModules();
        if (!this.licensedModules) {
            this.licensedModules = { dataLabelingModules, researchModules };
        }
        const dataLabelingIds = dataLabelingModules.map((module) => module.id);
        const researchIds = researchModules.map((module) => module.id);
        if (!this.authStore.canUserView("Data Labeling")) {
            this.setConversationLabels(this.dataLabels.filter((label) => !dataLabelingIds.includes(label.id)));
            filters.value.containsDataLabels = undefined;
        }

        if (!this.authStore.canUserView("Research")) {
            this.setConversationLabels(this.dataLabels.filter((label) => !researchIds.includes(label.id)));
            filters.value.containsResearchLabels = undefined;
        }

        if (!this.authStore.canUserView("AI Search")) {
            this.setEmbeddedTranscriptionQuery(undefined);
        }

        if (!this.authStore.canUserView("Patient Journey")) {
            this.setPatientJourneyStages([]);
        }

        if (!this.authStore.canUserView("Model Results")) {
            this.setModelResults([]);
        }

        if (!this.authStore.canUserView("Module Scores")) {
            this.setModuleScores([]);
        }

        if (this.authStore.canUserView("Restrict Evaluations") && !this.authStore.isUserUltra()) {
            this.setModuleScores([]);
            this.setEvaluationTypes([])
            this.setQuestionResults([]);
        }

        // Model Results is visible as a whole, so access is decided per row by the model
        // each row names: models in the "SE Suite" classifier category additionally require
        // the Safety Event permission, and the server withholds them from the catalog.
        // Drop saved rows naming a model that is no longer in the catalog, so a shared or
        // saved filter never renders a chip for a model missing from its own dropdown.
        if (this.modelResults?.length) {
            const valuesStore = this.rootStore.getStore(
                ApplicationFilterValuesStore,
            );
            if (!valuesStore.llmClassifiers) {
                await valuesStore.loadLLMClassifiers();
            }

            const allowedIds = new Set(
                (valuesStore.llmClassifiers ?? []).map((c) => `${c.id}`),
            );

            // Only filter once a catalog actually arrived; on a failed load leave the
            // selections alone rather than stripping them on a guess.
            if (allowedIds.size > 0) {
                this.setModelResults(
                    this.modelResults.filter((modelResult) =>
                        allowedIds.has(`${modelResult.id}`),
                    ),
                );
            }
        }

        // Question Results is visible as a whole, so access is decided per row by
        // the category of the module each row names. Drop the rows this viewer
        // cannot see, the same way Conversation Labels above drops labels whose
        // module they cannot see, so the filter panel never renders a row whose
        // module is missing from its own dropdown.
        if (this.questionResults?.length) {
            const valuesStore = this.rootStore.getStore(
                ApplicationFilterValuesStore,
            );
            await valuesStore.loadQuestionResultFilterableModules();

            this.setQuestionResults(
                this.questionResults.filter((questionResult) =>
                    valuesStore.canUseQuestionResultModule(
                        questionResult.licensedModuleId,
                    ),
                ),
            );
        }

        this.applicationFiltersService
            .willReceiveFullData(filters.value)
            .then((willReceiveFullData) => {
                if (willReceiveFullData) return;
                this.rootStore.getStore(MessageStore).logWarning("Some data not shown, due to user permissions.")
            })
    
        // Previously the selected filter was getting cleared since the quick filters were setting the date,
        // triggering an "update" and clearing the selected filter.
        const effectiveSelectedFilterState = _.cloneDeep(
            this.toFilterObject(),
        );

        if (!filters.value.startDate && !filters.value.endDate) {
            effectiveSelectedFilterState.startDate =
                this.quickFiltersStore.startDate;
            effectiveSelectedFilterState.endDate =
                this.quickFiltersStore.endDate;
            effectiveSelectedFilterState.dateReferenceOption =
                this.quickFiltersStore.dateReferenceOption;
            effectiveSelectedFilterState.dateRangeLabel =
                this.quickFiltersStore.dateRangeLabel;
        }

        this.saveExternalFilterState("Update", effectiveSelectedFilterState);
    }

    private async getLicensedModules() {
        if (this.licensedModules) {
            return this.licensedModules;
        }
        const res =
            await this.moduleService.getConversationLicensedModules();
            const canViewDataLabelingModules =
                this.authStore.canUserView("Data Labeling");
            const canViewResearchModules =
                this.authStore.canUserView("Research");

            const dataLabelingModules = res.filter(
                (module) =>
                    module.moduleCategory.name === "Data Labeling" &&
                    canViewDataLabelingModules,
            );
            const researchModules = res.filter(
                (module) =>
                    module.moduleCategory.name === "Research" &&
                    canViewResearchModules,
            );

            return {
                dataLabelingModules,
                researchModules,
            }
        }

    @action
    public async saveCurrentFilters(
        name: string,
        domain: ApplicationFilterDomain = ApplicationFilterDomain.User,
    ): Promise<SavedFilter | AsyncTaskStatus.Error> {
        return this.saveFilters(this, name, domain);
    }

    @action
    public async saveFilters(
        filters: ApplicationFiltersInstanceStore,
        name: string,
        domain: ApplicationFilterDomain = ApplicationFilterDomain.User,
    ) {
        return this.setupAsyncTask(
            ApplicationFiltersStore.Tasks.SAVE_FILTER,
            async () => {
                const createFilter: CreateApplicationFilter = {
                    name,
                    value: filters.toFilterObject(),
                    domain,
                };

                if (!this.savedFiltersByDomain[domain])
                    this.savedFiltersByDomain[domain] = [];

                const savedFilter =
                    await this.applicationFiltersService.createApplicationFilter(
                        createFilter,
                    );

                this.addSavedFiltersByDomain(savedFilter);

                return savedFilter;
            },
        );
    }

    @action
    public async updateFilters(
        id: string,
        filters: ApplicationFiltersInstanceStore,
        name: string,
        domain: ApplicationFilterDomain,
        applicationFilterValuesStore: ApplicationFilterValuesStore,
        applicationFilterSubscriptionId?: string,
    ) {
        return this.setupAsyncTask(
            ApplicationFiltersStore.Tasks.UPDATE_FILTER,
            async () => {
                const filterObj = filters.toFilterObject();
                const updateFilter: UpdateApplicationFilter = {
                    id,
                    name,
                    domain,
                    value: filterObj,
                    applicationFilterSubscriptionId
                };

                if (applicationFilterSubscriptionId) {
                    const filterHtml = await generateFilterHtml({
                        filters: filterObj,
                        applicationFiltersStore: this,
                        applicationFilterValuesStore
                    })
                    updateFilter.filterHtml = filterHtml;
                }

                await this.applicationFiltersService.updateApplicationFilter(
                    updateFilter,
                );

                filters.saveCurrentFilterState("Update");      
                
                // this ensures that when a filter is updated with a new domain is 
                // in the correct array in `savedFiltersByDomain` 
                const current = this.savedFilters.find((f) => f.id === id)!
                // remove from old domain
                this.savedFiltersByDomain[current.domain] = this.savedFiltersByDomain[current.domain].filter((f) => f.id !== id);
                // add to new domain
                this.savedFiltersByDomain[domain].push({...current, value: filterObj, name, domain });

                for (const filtersByDomain of Object.values(
                    this.savedFiltersByDomain,
                )) {
                    for (let i = 0; i < filtersByDomain.length; i++) {
                        if (filtersByDomain[i].id !== id) {
                            continue;
                        } else {
                            const updated = {
                                ...filtersByDomain[i],
                                value: filterObj,
                                name,
                                domain
                            }
                            filtersByDomain[i] = updated
                            if (this.selectedSavedFilter && this.selectedSavedFilter.id === updated.id)
                                runInAction(() => (this.selectedSavedFilter = updated));
                            return;
                        }
                    }
                }
            },
        );
    }

    @action
    public async updateSelectedSavedFilterWith(
        instance: ApplicationFiltersInstanceStore = this,
        applicationFilterValuesStore: ApplicationFilterValuesStore,
        name?: string,
        domain?: ApplicationFilterDomain): Promise<boolean> {
        const savedFilter = this.selectedSavedFilter;

        if (!savedFilter || !savedFilter.id)
            return false;

        const result =
            await this.updateFilters(
                savedFilter.id,
                instance, 
                name ?? savedFilter.name,
                domain ?? savedFilter.domain,
                applicationFilterValuesStore,
                savedFilter.applicationFilterSubscriptionId
            );

  

        return result !== AsyncTaskStatus.Error;
    }

    @action
    public async deleteFilters(ids: string[]) {
        const result = await this.setupAsyncTask(ApplicationFiltersStore.Tasks.DELETE_FILTER, async () => {
            try {
                return this.applicationFiltersService.deleteApplicationFilters(ids)
            } catch (e: any) {
                if (e instanceof ServiceError && e.errorMessage) {
                    this.rootStore.getStore(MessageStore).logError(e.errorMessage);
                }
                return AsyncTaskStatus.Error;
            }
        })

        const success = result !== AsyncTaskStatus.Error;
        if (!success) return false

        for (const [domain, filters] of Object.entries(this.savedFiltersByDomain)) {
            this.savedFiltersByDomain[domain] = filters.filter((f) => !ids.includes(f.id))
        }

        if (this.selectedSavedFilter && ids.includes(this.selectedSavedFilter.id))
            this.setSelectedSavedFilters();

        return true;
    }

    @action
    public setSelectedSavedFilters(
        filters?: SavedFilter,
    ) {
        this.selectedSavedFilter = filters;
    }

    @action
    public addSavedFiltersByDomain(
        filters: SavedFilter | SavedFilter[],
        domain?: ApplicationFilterDomain,
    ) {
        const filtersArray = Array.isArray(filters) ? filters : [filters];
        for (const filter of filtersArray) {
            const existingSavedFilters =
                this.savedFiltersByDomain[domain ?? filter.domain] ?? [];

            existingSavedFilters.push(filter);
            this.savedFiltersByDomain[domain ?? filter.domain] =
                existingSavedFilters;
        }
    }

    @action
    public setSavedFilters(filters: SavedFilter[]) {
        this.resetSavedFiltersByDomain();

        for (const filter of filters) {
            this.savedFiltersByDomain[filter.domain].push(filter);
        }

        this.forceUpdateSelectedSavedFilter();
    }

    /**
     * Finds the associated saved filter in the
     * `savedFilters` array (using id) and updates the
     * selected saved filter with that value
     * @returns
     */
    @action
    public forceUpdateSelectedSavedFilter() {
        if (!this.selectedSavedFilter) return;

        const id = this.selectedSavedFilter.id;
        if (!id) return;

        this.selectedSavedFilter = this.savedFilters.find(
            (filter) => filter.id === id,
        );
    }

    public getSavedFilterById(id?: string): SavedFilter | undefined {
        if (!id) return;

        return this.savedFilters.find((filter) => filter.id === id)
    }

    private instanceCounter: number = 0;
    /**
     * Returns a new ApplicationFiltersInstanceStore where you can
     * freely modify the filter values without affecting the global filters
     * contained within this store
     */
    public toInstance(): ApplicationFiltersInstanceStore {
        const instance = new ApplicationFiltersInstanceStore(
            this.rootStore,
            `Application Filters Instance Store ${++this.instanceCounter}`,
        );

        instance.setHierarchyLabels(this.getKnownHierarchyLabels());
        instance.applyExternalFilters(_.cloneDeep(super.toFilterObject()));

        return instance;
    }

    protected toQuickFilters(): ApplicationFiltersInstanceStore {
        const instance = new ApplicationFiltersInstanceStore(this.rootStore, "Quick Filters Store")

        // we don't allow quick filters to have an undefined date.
        const filters = _.cloneDeep(super.toFilterObject());
        if (!filters.startDate && !filters.endDate) {
            filters.startDate = instance.startDate;
            filters.endDate = instance.endDate;
        }

        instance.setHierarchyLabels(this.getKnownHierarchyLabels());
        instance.applyExternalFilters(filters);

        return instance;
    }

    public override toFilterObject(): ApplicationFilters {        
        const filterObject = super.toFilterObject();
        if (!this.quickFiltersStore) return filterObject;

        filterObject.startDate = this.quickFiltersStore.startDate;
        filterObject.endDate = this.quickFiltersStore.endDate;
        filterObject.dateReferenceOption =
            this.quickFiltersStore.dateReferenceOption;
        (filterObject as ApplicationFilters & {
            dateReference?: DateReferenceOption;
        }).dateReference = this.quickFiltersStore.dateReferenceOption;
        filterObject.dateRangeLabel = this.quickFiltersStore.dateRangeLabel;
        filterObject.hierarchyIds = this.quickFiltersStore.hierarchyIds;
        filterObject.selectedCategories = this.quickFiltersStore.selectedCategories;
        filterObject.selectedSubcategories = this.quickFiltersStore.selectedSubcategories;
        return filterObject;
    }

    private quickFilterKeys: FilterKey[] = ["dateRange", "dateReferenceOption", "hierarchyIds"]

    public alertOfQuickFilterOverride(filterKey: FilterKey) {
        if (this.appliedFilterCount === 0) return;
        if (filterKey === "hierarchyIds" && (!this.hierarchyIds || this.hierarchyIds.length === 0)) return;
        if (this.quickFilterKeys.includes(filterKey))
            this.rootStore.getStore(MessageStore).logInfo("Page-wide quick filters are overriding at least one filter selection.")
    }

    public override applyExternalFilters(
        filters: ApplicationFilters,
        applyToQuickFilters?: boolean,
        hydrateHierarchyLabels: boolean = true,
    ): void {
        const normalizedDateReferenceOption =
            filters.dateReferenceOption ??
            (filters as ApplicationFilters & {
                dateReference?: DateReferenceOption;
            }).dateReference;

        super.applyExternalFilters(filters, hydrateHierarchyLabels);
        if (!this.quickFiltersStore || !applyToQuickFilters) return;
        
        this.quickFiltersStore.setStartDate(filters.startDate);
        this.quickFiltersStore.setEndDate(filters.endDate);
        this.quickFiltersStore.setDateReferenceOption(
            normalizedDateReferenceOption,
        );
        this.quickFiltersStore.setDateRangeLabel(filters.dateRangeLabel ?? "");
        this.quickFiltersStore.setHierarchyIds(filters.hierarchyIds ?? []);
        if (hydrateHierarchyLabels) {
            void this.quickFiltersStore.hydrateHierarchyLabels(
                filters.hierarchyIds ?? [],
            );
        }
        this.quickFiltersStore.setSelectedCategories(filters.selectedCategories ?? [], filters.selectedSubcategories ?? []);
    }

    public isDefaultFilterValue = (
        key: string,
        givenFilters?: ApplicationFilters,
    ): boolean => {
        const filters = givenFilters || this.toFilterObject();
        switch (key) {
            case "dateRange":
            case "startDate":
            case "endDate":
                const currentBeginDate = givenFilters
                    ? (filters.startDate as Moment)
                    : this.quickFiltersStore
                          .datePickerStore.beginDate;
                const defaultBeginDate = moment()
                    .subtract(7, "days")
                    .startOf("day");
                const currentEndDate = givenFilters
                    ? (filters.endDate as Moment)
                    : this.quickFiltersStore
                          .datePickerStore.endDate;
                const defaultEndDate = moment().endOf("day");

                return (
                    currentBeginDate.isSame(defaultBeginDate, "day") &&
                    currentEndDate.isSame(defaultEndDate, "day")
                );
            case "callDuration":
                return (
                    JSON.stringify(
                        this.callDuration,
                    ) === JSON.stringify([0, 0])
                );
            case "maxCallDuration":
                return (
                    (filters.minCallDuration ?? 0) === 0 &&
                    (filters.maxCallDuration ?? 0) === 0
                );
            case "minCallDuration":
                return (
                    filters.minCallDuration === 0 ||
                    filters.minCallDuration == null
                );
            case "messageCount":
                return (
                    JSON.stringify(
                        this.messageCount,
                    ) === JSON.stringify([0, 0])
                );
            case "maxMessageCount":
                return (
                    (filters.minMessageCount ?? 0) === 0 &&
                    (filters.maxMessageCount ?? 0) === 0
                );
            case "minMessageCount":
                return (
                    filters.minMessageCount === 0 ||
                    filters.minMessageCount == null
                );
            case "hierarchy":
                return (
                    this.quickFiltersStore.hierarchyIds
                        .length === 0
                );
            case "hierarchyIds":
                return filters.hierarchyIds.length === 0;
            case "classifiers":
            case "rbcFilterItems":
                return isEqual(filters.rbcFilterItems, initRBCFilterItems);
            case "outline":
            case "outlineSegmentFilterItems":
                return (
                    (filters.outlineSegmentFilterItems ?? []).filter(
                        (item) => item.segments.length > 0,
                    ).length === 0
                );
            case "wordsOrPhrases":
            case "wordsAndPhrasesItems":
                return filters.wordsAndPhrasesIsInputStandard
                    ? isEqual(
                          filters.wordsAndPhrasesItems,
                          initWordsPhrasesItems,
                      )
                    : !filters.wordsAndPhrasesSearchString;
            case "evaluationType":
            case "evaluationTypes":
                return filters.evaluationTypes.length === 0;
            case "conversationLabeling":
            case "dataLabels":
                return filters.dataLabels?.length === 0;
            case "mediaType":
            case "mediaTypes":
                return filters.mediaTypes.length === 0;
            case "dateReferenceOption":
                return (
                    filters.dateReferenceOption ===
                    DateReferenceOption.InteractionDate
                );
            case "callDirection":
            case "callDirections":
                return filters.callDirections.length === 0;
            case "clientCallId":
                return !filters.clientCallId;
            case "agents":
            case "agentIds":
                return filters.agentIds?.length === 0;
            case "agentManagers":
            case "agentManagerIds":
                return filters.agentManagerIds?.length === 0;
            case "favoriteConversations":
                return !filters.favoriteConversations;
            case "language":
            case "languages":
                return filters.languages?.length === 0;
            case "semanticSearch":
            case "embeddedTranscriptionQuery":
            case "embeddedTranscriptionQueryThreshold":
                return (
                    filters.embeddedTranscriptionQueryThreshold === 0.4 &&
                    !filters.embeddedTranscriptionQuery
                );
            case "eddyEffectSignal":
            case "eddyEffectStatus":
                return (
                    filters.eddyEffectStatus === undefined ||
                    filters.eddyEffectStatus === null
                );
            case "startingSentiment":
            case "beginningSentiment":
                return (
                    filters.beginningSentiment === undefined ||
                    filters.beginningSentiment === null
                );
            case "contactType":
            case "contactTypes":
                return filters.contactTypes?.length === 0;
            case "patientJourney":
            case "patientJourneyStages":
                return filters.patientJourneyStages?.length === 0;
            case "modelResults":
                const modelResults = filters.modelResults;
                return !modelResults || modelResults?.length === 0;
            case "moduleScores":
                const moduleScores = filters.moduleScores;
                return !moduleScores || moduleScores?.length === 0;
            case "questionResults":
                const questionResults = filters.questionResults;
                return (
                    !questionResults ||
                    questionResults.filter((x) => x.tagIds.length > 0)
                        .length === 0
                );
            case "primaryTheme":
            case "topics":
                return (
                    filters.topics?.length === 0 &&
                    filters.containsTopics === true
                );
            case "themes":
                return (
                    (filters.themes?.length ?? 0) === 0 &&
                    (filters.containsThemes ?? true) === true
                );
            case "primaryTopic":
            case "primaryTopics":
                return (
                    filters.primaryTopics?.length === 0 &&
                    filters.containsPrimaryTopics === true
                );
            case "primaryDriver":
            case "drivers":
                return (
                    (filters.drivers?.length ?? 0) === 0 &&
                    (filters.containsDrivers ?? true) === true
                );
            case "hipaaCompliance":
                return (
                    filters.hipaaCompliance === undefined ||
                    filters.hipaaCompliance === null
                );
            case "endingSentiment":
                return (
                    filters.endingSentiment === undefined ||
                    filters.endingSentiment === null
                );
            case "safetyEvent":
                const safetyEvent = filters.safetyEvent;
                return !safetyEvent ||
                    (safetyEvent.identification === null &&
                        safetyEvent.types.length === 0 &&
                        safetyEvent.acknowledgments.length === 0);
            default:
                return true;
        }
    };

    static conversationsEvalTypeConversion(
        evalType: ConversationsEvalType | null,
        hasEval?: boolean,
    ): EvaluationType[] {
        // @ts-expect-error at evalType === ""
        if (hasEval !== undefined && (evalType === null || evalType === ""))
            return hasEval ? [] : [EvaluationType.NotEvaluated];

        const evaluationTypes: EvaluationType[] = [];

        switch (evalType) {
            case ConversationsEvalType.BottomOfTheFunnel:
                evaluationTypes.push(EvaluationType.HumanEvaluated);
                break;
            case ConversationsEvalType.MiddleOfTheFunnel:
                evaluationTypes.push(EvaluationType.AIEvaluated);
                break;
        }

        return evaluationTypes;
    }

    static conversationsHipaaConversion(
        hipaa: "Compliant" | "Not Compliant" | "N/A" | null,
    ) {
        switch (hipaa) {
            case "Compliant":
                return HipaaCompliance.Compliant;
            case "Not Compliant":
                return HipaaCompliance.NotCompliant;
            case "N/A":
                return HipaaCompliance.NotApplicable;
        }
    }

    static conversationsSentimentConversion(
        sentiment: "Positive" | "Neutral" | "Negative" | null,
    ) {
        switch (sentiment) {
            case "Positive":
                return Sentiment.Positive;
            case "Neutral":
                return Sentiment.Neutral;
            case "Negative":
                return Sentiment.Negative;
        }
    }

    static conversationsCallDirectionConversion(
        callDirection: "Inbound" | "Outbound" | "Transfer",
    ) {
        switch (callDirection) {
            case "Inbound":
                return CallDirection.Inbound;
            case "Outbound":
                return CallDirection.Outbound;
            case "Transfer":
                return CallDirection.Transfer;
        }
    }

    static conversationsMediaTypeConversion(
        mediaType: "Audio" | "ProcessedChat",
    ) {
        switch (mediaType) {
            case "Audio":
                return MediaType.Audio;
            case "ProcessedChat":
                return MediaType.ProcessedChat;
        }
    }

    static savedConversationsFiltersToApplicationFilters(
        filters: SavedConversationsFilters["value"],
    ): ApplicationFilters {
        return {
            startDate: filters.startDate,
            endDate: filters.endDate,
            dateReferenceOption: filters.dateReference,
            dateRangeLabel: filters.dateRangeLabel,
            minCallDuration: filters.callDuration?.[0],
            maxCallDuration: filters.callDuration?.[1],
            evaluationTypes:
                ApplicationFiltersStore.conversationsEvalTypeConversion(
                    filters.evalType,
                    filters.hasEval,
                ),
            hierarchyIds: filters.hierarchyIds,
            agentIds: filters.agentIds,
            mediaTypes: filters.mediaType.map(
                ApplicationFiltersStore.conversationsMediaTypeConversion,
            ),
            callDirections: filters.callDirection.map(
                ApplicationFiltersStore.conversationsCallDirectionConversion,
            ),
            clientCallId: filters.clientCallId,
            meta1: filters.meta1,
            meta2: filters.meta2,
            meta3: filters.meta3,
            meta4: filters.meta4,
            meta5: filters.meta5,
            extendedMetadata: filters.extendedMetadataFields?.reduce(
                (previous, item) => {
                    previous[item.name] = item.value;
                    return previous;
                },
                {},
            ),
            beginningSentiment:
                ApplicationFiltersStore.conversationsSentimentConversion(
                    filters.begSentiment,
                ),
            endingSentiment:
                ApplicationFiltersStore.conversationsSentimentConversion(
                    filters.endSentiment,
                ),
            eddyEffectStatus: filters.hasEddyEffect,
            safetyEvent: filters.safetyEvent,
            hipaaCompliance:
                ApplicationFiltersStore.conversationsHipaaConversion(
                    filters.hipaa,
                ),
            topics: filters.topicsFilter?.topics,
            primaryTopics: filters.primaryTopicsFilter?.topics,
            containsTopics: filters.topicsFilter?.contains,
            containsPrimaryTopics: filters.topicsFilter?.contains,
            // The legacy Conversations envelope predates the Primary Driver Theme, so there is
            // nothing to migrate into `themes` — it starts empty, exactly like `drivers`.
            themes: [],
            containsThemes: true,
            drivers: [],
            containsDrivers: true,
            wordsAndPhrasesItems: initWordsPhrasesItems,
            wordsAndPhrasesIsInputStandard: false,
            wordsAndPhrasesSearchString: filters.searchPhrase,
            rbcFilterItems: filters.RBCFilterItems,
            contactTypes: filters.contactTypes,
            patientJourneyStages: filters.patientJourneyStages,
            languages: filters.languages,
            dataLabels: []
        };
    }

    static backupConversationsFiltersToApplicationFilters(
        backupFilters: BackupFilters,
    ) {
        const combinedFilters: SavedConversationsFilters["value"] = {
            ...backupFilters.filterObject,
            startDate: backupFilters.beginDate,
            endDate: backupFilters.endDate,
            dateReference: backupFilters.referenceOption,
        };

        const filters =
            ApplicationFiltersStore.savedConversationsFiltersToApplicationFilters(
                combinedFilters,
            );
        filters.wordsAndPhrasesItems = backupFilters.wordsPhrases.items;
        filters.wordsAndPhrasesIsInputStandard =
            backupFilters.wordsPhrases.isInputStandard;

        return filters;
    }
}
